import React, { useEffect } from 'react';
import { Select, Table, Tooltip, Button } from 'antd';
import { formatDateTime, transformName } from 'src/utils/utils';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import Navbar from 'src/common/Navbar/Navbar';
import { fetchDownloadRequests } from './DownloadRequests.actions';
import { DownloadRequestsStyles } from './DownloadRequests.styles';
import {
  SET_DOWNLOAD_REQUESTS_FILTERS,
} from 'src/common/actions/constants';
import { useGenericState } from 'src/library/UseGenericState';

// Dump Type Constants
const DUMP_TYPES = [
  { key: 'VENDOR_PAYOUT', label: 'Vendor Payout' },
  { key: 'VENDOR_INVOICE', label: 'Vendor Invoice' },
];

interface DownloadRequestsProps {
  classes: any;
  downloadRequests: any;
  setFilters: (filters: any) => void;
  fetchDownloadRequests: (data: any) => void;
  t: (key: string) => string;
}

interface DownloadRequestRecord {
  status: string;
  created_at: string;
  url: string | null;
  filter_params: any;
  dump_type: string;
  request_completion_time: string;
}

const DownloadRequests: React.FC<DownloadRequestsProps> = (props) => {
  const {
    classes,
    downloadRequests,
    setFilters,
    fetchDownloadRequests,
    t,
  } = props;

  const { filters, data, loader } = downloadRequests;
  const downloadRequestsList = data?.downloadRequests || [];
  const isNextPresent = data?.isNextPresent || false;

  // Read dumpType from URL query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const dumpTypeFromUrl = urlParams.get('dumpType') || 'all';

  const [state, setState] = useGenericState({
    selectedDumpType: dumpTypeFromUrl,
    currentPage: 1,
    pageSize: 10,
  });

  const { selectedDumpType, currentPage, pageSize: currentPageSize } = state;

  // Fetch data on mount and when filters change
  useEffect(() => {
    const requestData = {
      dumpType: selectedDumpType === 'all' ? undefined : selectedDumpType,
      page: currentPage,
      pageSize: currentPageSize,
    };
    fetchDownloadRequests(requestData);
  }, [selectedDumpType, currentPage, currentPageSize]);

  const handleDumpTypeChange = (value: string) => {
    setState({
      selectedDumpType: value,
      currentPage: 1, // Reset to first page when filter changes
    });
    setFilters({
      ...filters,
      dumpType: value,
    });
  };

  const handleTableChange = (paginationConfig: any) => {
    setState({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleRefresh = () => {
    const requestData = {
      dumpType: selectedDumpType === 'all' ? undefined : selectedDumpType,
      page: currentPage,
      pageSize: currentPageSize,
    };
    fetchDownloadRequests(requestData);
  };

  const getStatusBadge = (status: string) => {
    let badgeClass = classes.statusBadge;
    switch (status?.toUpperCase()) {
      case 'PENDING':
        badgeClass += ` ${classes.statusPending}`;
        break;
      case 'FAILED':
        badgeClass += ` ${classes.statusFailed}`;
        break;
      case 'SUCCESS':
      case 'COMPLETED':
        badgeClass += ` ${classes.statusSuccess}`;
        break;
      default:
        badgeClass += ` ${classes.statusPending}`;
    }

    return <span className={badgeClass}>{status?.toUpperCase() || 'UNKNOWN'}</span>;
  };

  const renderFilterParams = (filterParams: Record<string, string>) => {
    if (!filterParams || typeof filterParams !== 'object') {
      return '-';
    }

    return (
      <div className={classes.filterText}>
        {Object.entries(filterParams).map(([key, value]) => (
          <div key={key}>
            <span className={classes.filterLabel}>{key}:</span> {value}
          </div>
        ))}
      </div>
    );
  };

  const handleDownload = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const renderDownloadButton = (url: string) => {
    return (
      <div>
        {url ? (
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(url)}
            className={classes.downloadButton}
          >
            Download
          </Button>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => formatDateTime(text, 'DD MMM YYYY HH:mm', '-'),
    },
    {
      title: 'Filters',
      dataIndex: 'filter_params',
      key: 'filter_params',
      width: 300,
      render: (filterParams: Record<string, string>) => renderFilterParams(filterParams),
    },
    {
      title: 'Type',
      dataIndex: 'dump_type',
      key: 'dump_type',
      width: 180,
      render: (text: string) => transformName(text || '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusBadge(status),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: DownloadRequestRecord) => renderDownloadButton(record.url),
    },
  ];

  const renderTypeFilter = () => (
    <Select
      className={classes.select}
      value={selectedDumpType}
      onChange={handleDumpTypeChange}
      placeholder="Select Type"
    >
      <Select.Option value="all">All Types</Select.Option>
      {DUMP_TYPES.map((type) => (
        <Select.Option key={type.key} value={type.key}>
          {type.label}
        </Select.Option>
      ))}
    </Select>
  );

  const renderNavbar = () => (
    <Navbar
      title={t('Download Requests')}
      rightComponents={() => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {renderTypeFilter()}
          <Tooltip title="Refresh">
            <Button onClick={handleRefresh} className={classes.refreshButton}>
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      )}
    />
  );

  return (
    <div className={classes.mainDiv}>
      {renderNavbar()}
      <div className={classes.tableContainer}>
        <Table
          rowKey={(record: DownloadRequestRecord, index) =>
            `${record.created_at}-${record.dump_type}-${index}`}
          className={classes.table}
          dataSource={downloadRequestsList}
          columns={columns}
          loading={loader}
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            total: isNextPresent
              ? currentPage * currentPageSize + 1 // More data exists
              : (currentPage - 1) * currentPageSize + downloadRequestsList.length, // Last page
            hideOnSinglePage: false,
            showTotal: (total, range) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setState({ currentPage: 1 })}
                  style={{
                    padding: '0 8px',
                    height: '24px',
                    fontSize: '12px',
                  }}
                >
                  ◀◀
                </Button>
                <span>Page {currentPage}</span>
              </div>
            ),
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  downloadRequests: state.downloadRequests,
});

const mapDispatchToProps = (dispatch) => ({
  setFilters: (data) =>
    dispatch({
      type: SET_DOWNLOAD_REQUESTS_FILTERS,
      data,
    }),
  fetchDownloadRequests: (data) => dispatch(fetchDownloadRequests(data)),
});

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: DownloadRequestsStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(DownloadRequests);
